import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { isTokenExpiringSoon, isTokenExpired } from '../utils/tokenUtils';
import { emitPermissionError } from '../utils/permissionErrorBus';

const AuthContext = createContext();

// Configuración de axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

axios.defaults.baseURL = API_BASE_URL;
// Sin timeout, si la API queda colgada (acepta la conexion pero nunca responde)
// la promesa no se resuelve jamas y el login se queda "cargando" indefinidamente.
axios.defaults.timeout = 15000;

// Variable para controlar si hay una renovación en proceso
let isRefreshing = false;
let refreshSubscribers = [];

// Función para notificar a los suscriptores cuando se renueva el token
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Función para notificar a todos los suscriptores
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// Función para renovar el token
const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post('/auth/refresh', {
      refresh_token: refreshToken
    });

    const { access_token, refresh_token } = response.data;

    // Guardar nuevos tokens
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', refresh_token);

    return access_token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    // Si falla la renovación, limpiar todo y redirigir
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw error;
  }
};

// Interceptor para incluir el token en las peticiones
axios.interceptors.request.use(
  async (config) => {
    // No verificar token en requests de login o refresh
    if (config.url.includes('/auth/login') || config.url.includes('/auth/refresh')) {
      return config;
    }

    const token = localStorage.getItem('token');

    if (token) {
      // Verificar si el token está por expirar (dentro de 5 minutos)
      if (isTokenExpiringSoon(token, 5)) {
        console.log('Token está por expirar, renovando...');

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await refreshAccessToken();
            isRefreshing = false;
            onTokenRefreshed(newToken);
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (error) {
            isRefreshing = false;
            return Promise.reject(error);
          }
        } else {
          // Si ya hay una renovación en proceso, esperar
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              config.headers.Authorization = `Bearer ${newToken}`;
              resolve(config);
            });
          });
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si es un error 401 y no es un request de login/refresh
    if (error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/login') &&
        !originalRequest.url.includes('/auth/refresh')) {

      originalRequest._retry = true;

      // Intentar renovar el token
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          onTokenRefreshed(newToken);

          // Reintentar la petición original con el nuevo token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      } else {
        // Si ya hay una renovación en proceso, esperar
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(originalRequest));
          });
        });
      }
    }

    // 403: al rol del usuario le falta el permiso que exige el endpoint.
    if (error.response?.status === 403) {
      emitPermissionError({
        url: originalRequest?.url,
        detail: error.response?.data?.detail
      });
    }

    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenCheckInterval = useRef(null);

  // Verificar token periódicamente
  const checkTokenExpiration = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Si el token está expirado, cerrar sesión
    if (isTokenExpired(token)) {
      console.log('Token expirado, cerrando sesión...');
      logout();
      return;
    }

    // Si el token está por expirar (en 5 minutos), renovarlo
    if (isTokenExpiringSoon(token, 5)) {
      console.log('Token próximo a expirar, renovando...');
      refreshAccessToken().catch(error => {
        console.error('Error al renovar token automáticamente:', error);
        logout();
      });
    }
  };

  // Refrescar el perfil (incluye permisos RBAC) desde /auth/me
  const refreshCurrentUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(response.data));
      setUser(response.data);
    } catch (error) {
      // Si falla, se mantiene el usuario almacenado en localStorage
      console.error('No se pudo refrescar el perfil:', error);
    }
  };

  useEffect(() => {
    // Verificar si hay token almacenado
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const userData = localStorage.getItem('user');

    if (token && refreshToken && userData) {
      // Verificar si el token aún es válido
      if (!isTokenExpired(token)) {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
        refreshCurrentUser();

        // Iniciar verificación periódica cada 1 minuto
        tokenCheckInterval.current = setInterval(checkTokenExpiration, 60000);
      } else {
        // Token expirado, intentar renovar
        console.log('Token expirado en inicio, intentando renovar...');
        refreshAccessToken()
          .then(() => {
            setIsAuthenticated(true);
            setUser(JSON.parse(userData));
            refreshCurrentUser();

            // Iniciar verificación periódica
            tokenCheckInterval.current = setInterval(checkTokenExpiration, 60000);
          })
          .catch(() => {
            // Si falla la renovación, limpiar todo
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
          });
      }
    }

    setLoading(false);

    // Limpiar interval al desmontar
    return () => {
      if (tokenCheckInterval.current) {
        clearInterval(tokenCheckInterval.current);
      }
    };
  }, []);

  const login = async (credentials) => {
    console.log('credentials', credentials);
    try {
      setLoading(true);

      // Usar el endpoint que acepta JSON
      const response = await axios.post('/auth/login-json', {
        email: credentials.username, // La API espera email
        password: credentials.password
      });

      // La API devuelve {access_token, refresh_token, token_type, expires_in}
      if (response.data.access_token) {
        const token = response.data.access_token;
        const refreshToken = response.data.refresh_token;

        // Obtener información del usuario
        const userResponse = await axios.get('/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const userData = userResponse.data;

        // Guardar en localStorage (incluyendo refresh token)
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(userData));

        // Actualizar estado
        setIsAuthenticated(true);
        setUser(userData);

        // Iniciar verificación periódica del token cada 1 minuto
        if (tokenCheckInterval.current) {
          clearInterval(tokenCheckInterval.current);
        }
        tokenCheckInterval.current = setInterval(checkTokenExpiration, 60000);

        return { success: true, user: userData };
      }

      return { success: false, message: 'Error de autenticación' };
    } catch (error) {
      console.error('Error de login:', error);

      let errorMessage = 'Error de conexión. Intenta nuevamente.';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'El servidor no responde. Verifica que la API esté activa.';
      } else if (error.response) {
        // El servidor respondió con un error
        switch (error.response.status) {
          case 422:
            errorMessage = 'Datos de login inválidos. Verifica usuario y contraseña.';
            break;
          case 401:
            errorMessage = 'Usuario o contraseña incorrectos.';
            break;
          case 400:
            errorMessage = 'Usuario inactivo.';
            break;
          default:
            errorMessage = error.response.data?.detail || 'Error del servidor.';
        }
      }

      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Detener verificación periódica
    if (tokenCheckInterval.current) {
      clearInterval(tokenCheckInterval.current);
    }

    // Limpiar localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Actualizar estado
    setIsAuthenticated(false);
    setUser(null);
  };

  // Función auxiliar para verificar permisos
  const hasPermission = (permission) => {
      if (!user) return false;
      // Los superusuarios tienen todos los permisos
      if (user.is_superuser || user.role === 'superuser') return true;
      // Verificar en el array de permisos si existe
      if (!user.permissions) return false;
      // Wildcard RBAC: '*' otorga todos los permisos
      if (user.permissions.includes('*')) return true;
      return user.permissions.includes(permission);
    };

  // Función auxiliar para verificar roles
  const hasRole = (role) => {
    if (!user || !user.role) return false;
    return user.role === role;
  };

  // Limpiar la obligación de cambio de contraseña tras un cambio exitoso
  const clearMustChangePassword = () => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    hasPermission,
    hasRole,
    clearMustChangePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
